/**
 * ZENO ERP — SERVICE TASK SERVICE
 * =================================
 * GOVERNED FILE — All service Firestore writes go through here.
 * UI never writes service_tasks directly.
 *
 * OFFLINE SUPPORT:
 * - startWork() + endJob() queue to localStorage if offline
 * - Sync runs on next online event
 *
 * TO-19 CHANGES (minimal):
 * - startWork() accepts start_attempt_at, start_location, start_location_at
 * - Online path: saves these 3 fields + start_location_timestamp
 * - Offline path: queues these 3 fields (no serverTimestamp in queue)
 *
 * TO-22 CHANGES:
 * - startWork() geofence check — 200m radius enforcement
 * - Site has no GPS → allow (backward compat)
 * - Offline → skip check, queue as before
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  increment,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
  SERVICE_STATUS,
  SERVICE_COLLECTIONS,
  OFFLINE_KEYS,
  COMPLETION_TYPE,
} from "../../constants/enums/serviceEnums";

const TASKS  = SERVICE_COLLECTIONS.TASKS;
const VISITS = SERVICE_COLLECTIONS.VISITS;
const LOGS   = SERVICE_COLLECTIONS.LOGS;

// ─────────────────────────────────────────────
// INTERNAL — HAVERSINE DISTANCE (meters)
// ─────────────────────────────────────────────
const getDistanceMeters = (lat1, lng1, lat2, lng2) => {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const GEOFENCE_RADIUS_METERS = 200;

// ─────────────────────────────────────────────
// INTERNAL — LOG STATUS CHANGE
// ─────────────────────────────────────────────
// D-298: company_id added — service_logs create rule needs it
// (writingSameCompany()); without it every log write was denied silently.
const logStatusChange = async (taskId, { company_id = "", from, to, changed_by, changed_by_name, reason = "" }) => {
  try {
    await addDoc(collection(db, LOGS), {
      company_id:      company_id || "",
      task_id:         taskId,
      from_status:     from,
      to_status:       to,
      changed_by:      changed_by      || "",
      changed_by_name: changed_by_name || "",
      reason:          reason          || "",
      created_at:      serverTimestamp(),
    });
  } catch (err) {
    console.error("logStatusChange error:", err);
  }
};

// ─────────────────────────────────────────────
// CREATE SERVICE TASK
// ─────────────────────────────────────────────
export const createServiceTask = async ({
  company_id,
  client_id, client_name,
  site_id, site_name,
  complaint,
  address        = "",
  location_link  = "",
  scheduled_date = "",
  job_category   = "",
  scheduled_time = "",
  machine_ids    = [],
  project_id     = "",
  project_name   = "",
  created_by, created_by_name,
}) => {
  if (!company_id)  throw new Error("company_id required");
  if (!complaint && job_category === "complaint") throw new Error("Complaint description required");
  if (!complaint && !job_category) throw new Error("Complaint required");
  if (!client_name) throw new Error("Client required");
  if (!site_name)   throw new Error("Site required");
  if (!scheduled_date) throw new Error("Scheduled date required");

  const ref = await addDoc(collection(db, TASKS), {
    company_id,
    client_id:       client_id || "",
    client_name,
    site_id:         site_id || "",
    site_name,
    complaint,
    address,
    location_link,
    scheduled_date,
    scheduled_time,
    job_category:    job_category || "",
    machine_ids:     machine_ids  || [],
    project_id:      project_id   || "",
    project_name:    project_name || "",
    status:          SERVICE_STATUS.OPEN,
    assigned_techs:  [],
    photos:          [],
    defective_items: [],
    created_by:      created_by      || "",
    created_by_name: created_by_name || "",
    created_at:      serverTimestamp(),
    updated_at:      serverTimestamp(),
  });

  return { success: true, task_id: ref.id };
};

// ─────────────────────────────────────────────
// ASSIGN TECHNICIANS
// ─────────────────────────────────────────────
export const assignTechnicians = async ({
  task_id,
  techs = [],
  assigned_by, assigned_by_name,
}) => {
  if (!task_id)      throw new Error("task_id required");
  if (!techs.length) throw new Error("At least one technician required");

  const taskRef  = doc(db, TASKS, task_id);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) throw new Error("Task not found");

  const current = taskSnap.data();

  const assigned_techs = techs.map(t => ({
    id:               t.id,
    name:             t.name || "",
    role:             t.role || "technician",
    accepted:         null,
    rejection_reason: "",
    assigned_at:      new Date().toISOString(),
  }));

  await updateDoc(taskRef, {
    assigned_techs,
    assigned_tech_ids: techs.map(t => t.id),
    status:            SERVICE_STATUS.ASSIGNED,
    assigned_by:       assigned_by      || "",
    assigned_by_name:  assigned_by_name || "",
    assigned_at:       serverTimestamp(),
    updated_at:        serverTimestamp(),
  });

  await logStatusChange(task_id, {
    company_id:      current.company_id,
    from:            current.status,
    to:              SERVICE_STATUS.ASSIGNED,
    changed_by:      assigned_by,
    changed_by_name: assigned_by_name,
  });

  return { success: true };
};

// ─────────────────────────────────────────────
// ACCEPT JOB (Tech)
// ─────────────────────────────────────────────
export const acceptJob = async ({
  task_id, tech_id, tech_name,
  filled_by = "", filled_by_name = "", filled_by_role = "", // audit — set when manager/owner accepts on behalf
}) => {
  if (!task_id) throw new Error("task_id required");
  if (!tech_id) throw new Error("tech_id required");

  const taskRef  = doc(db, TASKS, task_id);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) throw new Error("Task not found");

  const data = taskSnap.data();
  let techs = (data.assigned_techs || []).map(t =>
    t.id === tech_id
      ? { ...t, accepted: true, accepted_at: new Date().toISOString(),
          accepted_by: filled_by || t.id, accepted_by_name: filled_by_name || tech_name || "",
          accepted_by_role: filled_by_role || "" }
      : t
  );

  // OLD DOC COMPAT
  if (techs.length === 0 && data.assigned_to === tech_id) {
    techs = [{
      id:               tech_id,
      name:             tech_name || "",
      role:             "technician",
      accepted:         true,
      accepted_at:      new Date().toISOString(),
      accepted_by:      filled_by || tech_id,
      accepted_by_name: filled_by_name || tech_name || "",
      accepted_by_role: filled_by_role || "",
      rejection_reason: "",
    }];
  }

  await updateDoc(taskRef, {
    assigned_techs: techs,
    updated_at:     serverTimestamp(),
  });

  return { success: true };
};

// ─────────────────────────────────────────────
// REJECT JOB (Tech)
// ─────────────────────────────────────────────
export const rejectJob = async ({ task_id, tech_id, tech_name, reason }) => {
  if (!task_id)          throw new Error("task_id required");
  if (!tech_id)          throw new Error("tech_id required");
  if (!reason?.trim())   throw new Error("Rejection reason is required");

  const taskRef  = doc(db, TASKS, task_id);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) throw new Error("Task not found");

  const data  = taskSnap.data();
  const techs = (data.assigned_techs || []).map(t =>
    t.id === tech_id
      ? { ...t, accepted: false, rejection_reason: reason.trim(), rejected_at: new Date().toISOString() }
      : t
  );

  await updateDoc(taskRef, {
    assigned_techs: techs,
    updated_at:     serverTimestamp(),
  });

  await logStatusChange(task_id, {
    company_id:      data.company_id,
    from:            data.status,
    to:              data.status,
    changed_by:      tech_id,
    changed_by_name: tech_name,
    reason:          `Rejected: ${reason}`,
  });

  return { success: true };
};

// ─────────────────────────────────────────────
// START WORK — OFFLINE CAPABLE
// TO-19: GPS fields
// TO-22: Geofence 200m enforcement
// ─────────────────────────────────────────────
export const startWork = async ({
  task_id,
  tech_id,
  tech_name,
  company_id,
  start_attempt_at  = null,
  start_location    = null,   // { lat, lng, accuracy } or null
  start_location_at = null,
  location_skipped  = null,   // "offline" | "gps_unavailable" | "manager_initiated" | null
  manager_initiated = false,  // true when a manager/owner is starting this on behalf of the tech (office-managed mode) — skips geofence check
  filled_by         = "",     // audit — uid of whoever actually clicked Start
  filled_by_name    = "",
  filled_by_role    = "",
}) => {
  if (!task_id) throw new Error("task_id required");

  const payload = {
    task_id, tech_id, tech_name, company_id,
    started_at_local:  new Date().toISOString(),
    start_attempt_at:  start_attempt_at  || new Date().toISOString(),
    start_location:    start_location    || null,
    start_location_at: start_location_at || null,
    location_skipped:  manager_initiated ? "manager_initiated" : (location_skipped || null),
  };

  // OFFLINE — queue to IndexedDB
  if (!navigator.onLine) {
    const { saveJobAction, saveJobReport } = await import("../../services/offlineDB");
    const ts = new Date().toISOString();

    await saveJobAction(task_id, "start", {
      uid:              tech_id            || "",
      name:             tech_name          || "",
      company_id:       company_id         || "",
      coords:           start_location     || null,
      timestamp:        ts,
      start_attempt_at: start_attempt_at   || ts,
      location_skipped: manager_initiated ? "manager_initiated" : (location_skipped || null),
      filled_by:        filled_by          || "",
      filled_by_name:   filled_by_name     || "",
      filled_by_role:   filled_by_role     || "",
    });

    await saveJobReport(task_id, { start_time_iso: ts });

    return { success: true, offline: true };
  }

  // ONLINE
  const taskRef  = doc(db, TASKS, task_id);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) throw new Error("Task not found");

  const data = taskSnap.data();

  // NOTE: Single-active-job restriction REMOVED (client requirement) —
  // technician can now have multiple concurrent in_progress jobs.

  // ── TO-22: GEOFENCE CHECK ──────────────────
  // Skipped entirely when manager_initiated=true — office-managed mode
  // has no meaningful "tech location" to check against (the person
  // clicking Start is in the office, not at the site).
  if (!manager_initiated && start_location && data.site_id) {
    try {
      const siteSnap = await getDoc(doc(db, "sites", data.site_id));
      if (siteSnap.exists()) {
        const site = siteSnap.data();
        if (site.lat && site.lng) {
          const distance = Math.round(getDistanceMeters(
            start_location.lat, start_location.lng,
            site.lat, site.lng
          ));
          if (distance > GEOFENCE_RADIUS_METERS) {
            throw new Error(
              `You are ${distance}m away from site "${data.site_name}". ` +
              `Must be within ${GEOFENCE_RADIUS_METERS}m to start work.`
            );
          }
        }
        // site has no GPS → allow (backward compat)
      }
    } catch (err) {
      if (err.message.includes("away from site")) throw err; // re-throw geofence error
      console.warn("Geofence check failed (non-blocking):", err);
    }
  }
  // start_location null → no GPS from tech → allow

  // ── TO-19: save GPS fields ─────────────────
  await updateDoc(taskRef, {
    status:                    SERVICE_STATUS.IN_PROGRESS,
    started_at:                serverTimestamp(),
    updated_at:                serverTimestamp(),
    start_attempt_at:          payload.start_attempt_at,
    start_location:            payload.start_location,
    start_location_at:         payload.start_location_at,
    start_location_timestamp:  serverTimestamp(),
    location_skipped:          payload.location_skipped,
    started_by:                filled_by      || tech_id || "",
    started_by_name:           filled_by_name || tech_name || "",
    started_by_role:           filled_by_role || "",
  });

  // CREATE VISIT LOG
  await addDoc(collection(db, VISITS), {
    task_id,
    company_id:       company_id || data.company_id || "",
    tech_id:          tech_id    || "",
    tech_name:        tech_name  || "",
    check_in_at:      serverTimestamp(),
    check_out_at:     null,
    duration_minutes: null,
    start_location:   payload.start_location,
    created_at:       serverTimestamp(),
  });

  await logStatusChange(task_id, {
    company_id:      data.company_id,
    from:            data.status,
    to:              SERVICE_STATUS.IN_PROGRESS,
    changed_by:      filled_by      || tech_id,
    changed_by_name: filled_by_name || tech_name,
  });

  // Job start hone pe assignment notification read mark karo
  try {
    // D-302: company_id filter — required by the notifications read rule.
    const notifSnap = await getDocs(query(
      collection(db, "notifications"),
      where("company_id", "==", data.company_id || ""),
      where("reference_id", "==", task_id),
      where("type", "==", "job_assigned"),
      where("is_read", "==", false)
    ));
    await Promise.all(
      notifSnap.docs.map(d =>
        updateDoc(doc(db, "notifications", d.id), {
          is_read: true,
          read_at: serverTimestamp(),
        })
      )
    );
  } catch (e) {
    console.warn("Notification read mark failed:", e);
  }

  return { success: true, offline: false };
};

// ─────────────────────────────────────────────
// END JOB — OFFLINE CAPABLE
// ─────────────────────────────────────────────
export const endJob = async ({
  task_id, tech_id, tech_name, company_id,
  completion_type, service_report, issue_found, resolution,
  defective_items       = [],
  return_items          = [],
  photos                = [],
  site_payment_received = false,
  site_payment_amount   = 0,
  site_payment_mode     = "",
  filled_by             = "",   // uid of whoever actually submitted this report
  filled_by_name        = "",   // name of same
  filled_by_role        = "",   // "manager" | "owner" | "sm" | "" (blank = technician, default path)
}) => {
  if (!task_id)                throw new Error("task_id required");
  if (!completion_type)        throw new Error("completion_type required");
  if (!service_report?.trim()) throw new Error("Service report required");

  const finalStatus = completion_type === COMPLETION_TYPE.COMPLETE
    ? SERVICE_STATUS.COMPLETED_PENDING_APPROVAL
    : SERVICE_STATUS.PARTIAL;

  const payload = {
    task_id, tech_id, tech_name, company_id,
    completion_type, service_report, issue_found,
    resolution, defective_items, return_items, photos,
    ended_at_local: new Date().toISOString(),
    final_status:   finalStatus,
    filled_by, filled_by_name, filled_by_role,
  };

  // OFFLINE — queue to IndexedDB
  if (!navigator.onLine) {
    const { saveJobAction, saveJobReport } = await import("../../services/offlineDB");
    const ts = new Date().toISOString();

    await saveJobAction(task_id, "complete", {
      uid:                   tech_id               || "",
      name:                  tech_name             || "",
      company_id:            company_id            || "",
      remarks:               service_report        || "",
      issue_found:           issue_found           || "",
      resolution:            resolution            || "",
      defective_items:       defective_items       || [],
      return_items:          return_items          || [],
      photos:                photos                || [],
      completion_type:       completion_type,
      site_payment_received: site_payment_received || false,
      site_payment_amount:   site_payment_amount   || 0,
      site_payment_mode:     site_payment_mode     || "",
      filled_by:             filled_by             || "",
      filled_by_name:        filled_by_name        || "",
      filled_by_role:        filled_by_role        || "",
      timestamp:             ts,
    });

    await saveJobReport(task_id, {
      remarks:         service_report  || "",
      defective_items: defective_items || [],
      completed_at:    ts,
    });

    return { success: true, offline: true };
  }

  // ONLINE
  const taskRef  = doc(db, TASKS, task_id);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) throw new Error("Task not found");

  const data = taskSnap.data();

  // ── VISIT DURATION ACCOUNTING ─────────────────────────────────────
  // service_visits is the ledger-first source of truth for time tracking
  // (Constitution: Ledger-First Rule — never edit, only accumulate).
  // service_tasks.duration_minutes is a CACHE — a running total that
  // increments each time a visit session closes here.
  //
  // This also fixes a real bug: naively diffing task.started_at vs
  // task.completed_at breaks the moment a job goes Partial → gets
  // reassigned → gets restarted, because the OLD completed_at ends up
  // earlier than the NEW started_at (negative duration). Each visit
  // session is self-contained, so summing closed sessions avoids this
  // entirely — no cross-session subtraction ever happens.
  let thisVisitDurationMinutes = 0;
  try {
    const openVisitSnap = await getDocs(query(
      collection(db, VISITS),
      where("company_id", "==", company_id || data.company_id || ""),
      where("task_id", "==", task_id),
      where("check_out_at", "==", null)
    ));
    if (!openVisitSnap.empty) {
      const visitDoc  = openVisitSnap.docs[0];
      const visitData = visitDoc.data();
      const checkIn = visitData.check_in_at?.toDate
        ? visitData.check_in_at.toDate()
        : new Date(visitData.check_in_at);
      thisVisitDurationMinutes = Math.max(0, Math.round((new Date() - checkIn) / 60000));
      await updateDoc(doc(db, VISITS, visitDoc.id), {
        check_out_at:     serverTimestamp(),
        duration_minutes: thisVisitDurationMinutes,
      });
    }
    // No open visit found (e.g. job was rejected-back and re-completed
    // without a fresh Start) — skip silently, nothing to close.
  } catch (visitErr) {
    console.warn("Visit duration accounting failed (non-blocking):", visitErr);
  }

  // Existing arrays ke saath merge karo — overwrite se pehle-added
  // defective items / return items / photos delete nahi hone chahiye
  const mergedDefectiveItems = [...(data.defective_items || []), ...defective_items];
  const mergedReturnItems    = [...(data.return_items    || []), ...return_items];
  const mergedPhotos         = [...(data.photos          || []), ...photos];

  await updateDoc(taskRef, {
    status:                finalStatus,
    completion_type,
    service_report:        service_report || "",
    issue_found:           issue_found    || "",
    resolution:            resolution     || "",
    defective_items:       mergedDefectiveItems,
    return_items:           mergedReturnItems,
    photos:                 mergedPhotos,
    completed_at:          serverTimestamp(),
    updated_at:            serverTimestamp(),
    duration_minutes:      increment(thisVisitDurationMinutes),
    site_payment_received: Boolean(site_payment_received),
    site_payment_amount:   site_payment_received ? Number(site_payment_amount || 0) : 0,
    site_payment_mode:     site_payment_received ? (site_payment_mode || "") : "",
    site_payment_by:       tech_name || "",
    site_payment_at:       site_payment_received ? serverTimestamp() : null,
    // D-298: who actually completed the job — tech's own Complete has
    // filled_by "", so fall back to tech_id (same pattern as started_by).
    completed_by:          filled_by      || tech_id   || "",
    completed_by_name:     filled_by_name || tech_name || "",
    completed_by_role:     filled_by_role || "",
    filled_by:             filled_by      || "",
    filled_by_name:        filled_by_name || "",
    filled_by_role:        filled_by_role || "",
  });

  // Cash collected notification — non-blocking
  if (site_payment_received && Number(site_payment_amount) > 0) {
    try {
      const { notifyCashCollected } = await import("../../services/notificationService");
      await notifyCashCollected({
        service_id:  task_id,
        tech_name:   tech_name         || "",
        client_name: data.client_name  || "",
        site_name:   data.site_name    || "",
        amount:      Number(site_payment_amount),
        mode:        site_payment_mode || "cash",
        company_id:  company_id || data.company_id || "",
      });
    } catch (notifErr) {
      console.warn("Cash notification failed (non-blocking):", notifErr);
    }
  }

  await logStatusChange(task_id, {
    company_id:      data.company_id,
    from:            data.status,
    to:              finalStatus,
    changed_by:      filled_by      || tech_id,
    changed_by_name: filled_by_name || tech_name,
    reason:          completion_type === COMPLETION_TYPE.PARTIAL ? "Partial completion" : "",
  });

  return { success: true, offline: false };
};

// ─────────────────────────────────────────────
// CLOSE JOB (SM/Owner)
// ─────────────────────────────────────────────
export const closeJob = async ({
  task_id,
  job_type,
  job_type_other,
  is_chargeable,
  chargeable_amount = 0,
  closed_by, closed_by_name,
}) => {
  if (!task_id)  throw new Error("task_id required");
  if (!job_type) throw new Error("Job type required");

  const taskRef  = doc(db, TASKS, task_id);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) throw new Error("Task not found");

  const data = taskSnap.data();

  const closeable = [SERVICE_STATUS.COMPLETED_PENDING_APPROVAL, "completed"];
  if (!closeable.includes(data.status)) {
    throw new Error("Task must be in completed_pending_approval status to close");
  }

  // amount_pending removed (16 Jul 2026) — it had no functional effect;
  // the Invoice Engine's Record Payment / Mark as Paid buttons already
  // show regardless of this flag, and actual payment-pending status is
  // now correctly derived from the invoice's own balance_amount instead.
  await updateDoc(taskRef, {
    status:            SERVICE_STATUS.CLOSED,
    job_type,
    job_type_other:    job_type === "other" ? (job_type_other || "") : "",
    is_chargeable:     Boolean(is_chargeable),
    chargeable_amount: is_chargeable ? Number(chargeable_amount || 0) : 0,
    closed_by:         closed_by      || "",
    closed_by_name:    closed_by_name || "",
    closed_at:         serverTimestamp(),
    updated_at:        serverTimestamp(),
  });

  await logStatusChange(task_id, {
    company_id:      data.company_id,
    from:            data.status,
    to:              SERVICE_STATUS.CLOSED,
    changed_by:      closed_by,
    changed_by_name: closed_by_name,
  });

  // ── FINANCIAL ENTRY — REMOVED FROM HERE (16 Jul 2026) ───────────────────
  // Previously, closing a chargeable job wrote directly to
  // financial_transactions + a "billing" collection doc. This was an
  // orphaned duplicate-engine violation: the real, fully-built Invoice
  // Engine (CreateInvoice.js → invoices collection → InvoiceDetail.js's
  // "Record Payment"/"Mark as Paid" flow) was never connected to it, so
  // a "billing" doc written here was never read by ANY UI page, and a
  // "payment_pending" financial_transactions entry written here had NO
  // way to ever be updated when the client later paid (financial_
  // transactions is immutable — Golden Rule — so it could only ever be
  // reversed/added-to via a NEW entry, and no such flow existed). This
  // is the root cause of the client complaint: 75%-credit jobs never
  // got their later payment recorded anywhere.
  //
  // NEW FLOW: closeJob() now ONLY closes the service_tasks record. The
  // caller (ServiceDetail.js) is responsible for navigating to
  // /billing/create with the line-item breakdown when is_chargeable is
  // true — reusing the SAME governed Invoice Engine every other invoice
  // in this system goes through. This is the single source of truth for
  // service income; do not reintroduce a parallel write path here.
  // See DECISION_LOG D-2XX.

  return { success: true, is_chargeable: Boolean(is_chargeable) };
};

// ─────────────────────────────────────────────
// REJECT COMPLETION (SM → back to in_progress)
// ─────────────────────────────────────────────
export const rejectCompletion = async ({
  task_id, reason, rejected_by, rejected_by_name,
}) => {
  if (!task_id)        throw new Error("task_id required");
  if (!reason?.trim()) throw new Error("Reason required");

  const taskRef  = doc(db, TASKS, task_id);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) throw new Error("Task not found");

  const data = taskSnap.data();

  await updateDoc(taskRef, {
    status:           SERVICE_STATUS.IN_PROGRESS,
    rejection_reason: reason,
    rejected_by:      rejected_by      || "",
    rejected_by_name: rejected_by_name || "",
    rejected_at:      serverTimestamp(),
    updated_at:       serverTimestamp(),
  });

  await logStatusChange(task_id, {
    company_id:      data.company_id,
    from:            data.status,
    to:              SERVICE_STATUS.IN_PROGRESS,
    changed_by:      rejected_by,
    changed_by_name: rejected_by_name,
    reason,
  });

  return { success: true };
};

// ─────────────────────────────────────────────
// ADD DEFECTIVE ITEM
// ─────────────────────────────────────────────
export const addDefectiveItem = async ({
  task_id,
  item_name,
  qty,
  machine_id           = "",
  machine_description = "",
  machine_model       = "",
  serial_no           = "",
  part_name           = "",
  part_code           = "",
  remarks             = "",
}) => {
  if (!task_id)   throw new Error("task_id required");
  if (!item_name) throw new Error("Item name required");

  const taskRef  = doc(db, TASKS, task_id);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) throw new Error("Task not found");

  const data     = taskSnap.data();
  const existing = data.defective_items || [];

  const newItem = {
    item_name:           item_name.trim(),
    qty:                 Number(qty || 1),
    machine_id:          machine_id || "",
    machine_description: machine_description.trim(),
    machine_model:       machine_model.trim(),
    serial_no:           serial_no.trim(),
    part_name:           part_name.trim(),
    part_code:           part_code.trim(),
    remarks:             remarks.trim(),
    oem_return_status:   "pending",
    added_at:            new Date().toISOString(),
  };

  await updateDoc(taskRef, {
    defective_items: [...existing, newItem],
    updated_at:      serverTimestamp(),
  });

  return { success: true };
};

// ─────────────────────────────────────────────
// ADD REMARK
// Notifies whichever side did NOT send the remark — technician gets
// notified if a manager/owner sent it, and vice versa. Single governed
// entry point — both ServiceDetail.js (manager) and TaskDetail.js
// (technician) call this, so notification logic only lives here once.
// ─────────────────────────────────────────────
export const addRemark = async ({ task_id, remark, added_by, added_by_name }) => {
  if (!task_id)        throw new Error("task_id required");
  if (!remark?.trim()) throw new Error("Remark required");

  const taskRef  = doc(db, TASKS, task_id);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) throw new Error("Task not found");

  const data    = taskSnap.data();
  const remarks = data.remarks_log || [];

  await updateDoc(taskRef, {
    remarks:     remark.trim(),
    remarks_log: [...remarks, {
      text:    remark.trim(),
      by:      added_by      || "",
      by_name: added_by_name || "",
      at:      new Date().toISOString(),
    }],
    updated_at: serverTimestamp(),
  });

  // Notify the other side (non-blocking — remark save should never fail
  // because a notification failed)
  try {
    const techId    = data.assigned_techs?.[0]?.id || data.assigned_to || "";
    const managerId = data.assigned_by || "";
    const isFromTech = added_by === techId;
    const recipientUid = isFromTech ? managerId : techId;

    if (recipientUid && recipientUid !== added_by) {
      const { notifyNewRemark } = await import("../../services/notificationService");
      await notifyNewRemark({
        service_id:  task_id,
        remark_text: remark.trim(),
        by_name:     added_by_name || "",
        for_uid:     recipientUid,
        client_name: data.client_name || "",
        site_name:   data.site_name   || "",
        company_id:  data.company_id  || "",
      });
    }
  } catch (notifErr) {
    console.warn("notifyNewRemark failed (non-blocking):", notifErr);
  }

  return { success: true };
};

// ─────────────────────────────────────────────
// GET TASKS — SM/Owner dashboard
// ─────────────────────────────────────────────
export const getServiceTasks = async ({ company_id, status_filter = null }) => {
  if (!company_id) throw new Error("company_id required");

  const q = status_filter
    ? query(collection(db, TASKS), where("company_id", "==", company_id), where("status", "==", status_filter))
    : query(collection(db, TASKS), where("company_id", "==", company_id));

  const snap  = await getDocs(q);
  const tasks = snap.docs.map(d => ({ id: d.id, ...d.data() }));

  return tasks.sort((a, b) => {
    const ta = a.created_at?.toDate?.()?.getTime?.() ?? a.created_at ?? 0;
    const tb = b.created_at?.toDate?.()?.getTime?.() ?? b.created_at ?? 0;
    return tb - ta;
  });
};

// ─────────────────────────────────────────────
// GET TECH TASKS — technician dashboard
// ─────────────────────────────────────────────
export const getTechTasks = async ({ company_id, tech_id }) => {
  if (!company_id) throw new Error("company_id required");
  if (!tech_id)    throw new Error("tech_id required");

  const newQ = query(
    collection(db, TASKS),
    where("company_id", "==", company_id),
    where("status", "in", [
      SERVICE_STATUS.ASSIGNED,
      SERVICE_STATUS.IN_PROGRESS,
      SERVICE_STATUS.PARTIAL,
      SERVICE_STATUS.COMPLETED_PENDING_APPROVAL,
    ])
  );

  const oldQ = query(
    collection(db, TASKS),
    where("company_id", "==", company_id),
    where("assigned_to", "==", tech_id)
  );

  const [newSnap, oldSnap] = await Promise.all([getDocs(newQ), getDocs(oldQ)]);

  const seen = new Set();
  const all  = [];

  for (const d of [...newSnap.docs, ...oldSnap.docs]) {
    if (!seen.has(d.id)) {
      seen.add(d.id);
      all.push({ id: d.id, ...d.data() });
    }
  }

  const TERMINAL = new Set([SERVICE_STATUS.CLOSED, SERVICE_STATUS.CANCELLED, "completed"]);

  return all.filter(task => {
    if (TERMINAL.has(task.status)) return false;
    if (task.assigned_techs?.length > 0) {
      return task.assigned_techs.some(t => t.id === tech_id);
    }
    return task.assigned_to === tech_id;
  });
};

